/*
 * Panel widgets in the look of the TP8 demo (tp8-visu): a 270-degree dial,
 * today's time program as a bar, the heating curve and a layered storage
 * tank. Each reads its points by binding key and draws inline SVG with the
 * theme's colours, so it works offline on a panel and in light and dark.
 */

import { useEffect, useState } from 'preact/hooks';
import type { DayScheduleWidget, DialWidget, HeatingCurveWidget, TankWidget } from '../widgets';
import type { IPointBindingSource } from '../binding';
import { pointSignal } from '../pointSignal';
import { isDegraded } from '../quality';
import { useLeafMeta } from '../LeafMetaContext';
import { formatLeafValue } from '../../format/leafValue';
import { evaluateCondition } from '../script/hmiScript';
import { useNamedPoints } from './namedPoints';
import { isNoSensor } from './PanelWidgets';
import './rich.css';

interface P<W> { widget: W; source: IPointBindingSource; }

/** Current raw value of one binding key; undefined when unbound or unknown. */
function usePoint(source: IPointBindingSource, key: string | null | undefined) {
  const sig = key ? pointSignal(source, key) : null;
  const release = sig?.release;
  useEffect(() => release, [release]);
  const current = sig?.value.value ?? null;
  const raw = current?.value;
  const num = typeof raw === 'number' && !isNoSensor(raw) ? raw : undefined;
  return { raw, num, current, degraded: current ? isDegraded(current) : false };
}

function fmtNum(v: number | undefined, decimals: number): string {
  if (v === undefined) return '–';
  return v.toLocaleString('de-AT', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function useMinuteNow(): Date {
  const [d, setD] = useState(() => new Date());
  useEffect(() => {
    const t = window.setInterval(() => setD(new Date()), 30_000);
    return () => window.clearInterval(t);
  }, []);
  return d;
}

// ------------------------------------------------------------ dial

export function DialRenderer({ widget, source }: P<DialWidget>) {
  const value = usePoint(source, widget.valuePoint);
  const marker = usePoint(source, widget.markerPoint);
  const meta = useLeafMeta(widget.valuePoint);
  const { min, max } = widget.max > widget.min ? widget : { min: 0, max: 100 };
  const decimals = widget.decimals ?? meta?.decimals ?? 1;
  const unit = widget.unit ?? meta?.unit ?? '';
  const degree = unit === '°' || unit === '°C';

  const R = 128, SW = 18, CX = 170, CY = 168, A0 = 135, SWEEP = 270;
  const C = 2 * Math.PI * R;
  const frac = (v: number | undefined) => (v === undefined ? 0 : Math.min(1, Math.max(0, (v - min) / (max - min))));
  const ang = (v: number) => A0 + SWEEP * frac(v);
  const polar = (deg: number, r: number) => {
    const a = (deg * Math.PI) / 180;
    return [CX + r * Math.cos(a), CY + r * Math.sin(a)] as const;
  };
  const rot = (deg: number) => ({ transform: `rotate(${deg}deg)`, transformOrigin: `${CX}px ${CY}px` });

  // Halbe Schritte, ganze mittel, jeder fuenfte lang; bei weitem Bereich nur ganze.
  const stepTick = max - min > 30 ? 1 : 0.5;
  const ticks = [];
  for (let t = Math.ceil(min / stepTick) * stepTick; t <= max + 1e-9; t += stepTick) {
    const major = Math.abs(t % 5) < 1e-9;
    const whole = Math.abs(t % 1) < 1e-9;
    const [x1, y1] = polar(ang(t), R + SW / 2 + 7);
    const [x2, y2] = polar(ang(t), R + SW / 2 + (major ? 19 : whole ? 13 : 9));
    const lit = value.num !== undefined && t <= value.num + 1e-9;
    ticks.push(<line key={t} x1={x1} y1={y1} x2={x2} y2={y2}
      class={`hv-dial__tick${major ? ' hv-dial__tick--major' : ''}${lit ? ' hv-dial__tick--lit' : ''}`} />);
  }
  const [lx, ly] = polar(A0, R + 4);
  const [rx, ry] = polar(A0 + SWEEP, R + 4);
  const f = frac(value.num);

  return (
    <div class={`hv-dial${value.degraded ? ' hv-dial--stale' : ''}`}>
      <svg viewBox="0 0 340 300" class="hv-dial__svg" aria-hidden="true">
        {ticks}
        <circle cx={CX} cy={CY} r={R} fill="none" class="hv-dial__track" stroke-width={SW} stroke-linecap="round"
          stroke-dasharray={`${(C * SWEEP) / 360} ${C}`} style={rot(A0)} />
        {/* Ein Verlauf auf dem gedrehten Kreis zeichnet Chrome nur am Anfang;
            darum volle Farbe und darunter ein weicher Schein. */}
        <circle cx={CX} cy={CY} r={R} fill="none" stroke-width={SW + 14} stroke-linecap="round"
          stroke-dasharray={`${Math.max(0.001, (C * SWEEP * f) / 360)} ${C}`} style={rot(A0)} class="hv-dial__arc hv-dial__glow" />
        <circle cx={CX} cy={CY} r={R} fill="none" stroke-width={SW} stroke-linecap="round"
          stroke-dasharray={`${Math.max(0.001, (C * SWEEP * f) / 360)} ${C}`} style={rot(A0)} class="hv-dial__arc hv-dial__fill" />
        {marker.num !== undefined && (
          <g style={rot(ang(marker.num))} class="hv-dial__mover">
            <circle cx={CX + R - SW / 2 - 14} cy={CY} r="5" class="hv-dial__marker" />
          </g>
        )}
        {value.num !== undefined && (
          <g style={rot(ang(value.num))} class="hv-dial__mover">
            <circle cx={CX + R} cy={CY} r="17" class="hv-dial__knob" />
            <circle cx={CX + R} cy={CY} r="6" fill="var(--accent)" />
          </g>
        )}
        <text x={lx} y={ly + 22} text-anchor="middle" class="hv-dial__end">{min}{degree ? '°' : ''}</text>
        <text x={rx} y={ry + 22} text-anchor="middle" class="hv-dial__end">{max}{degree ? '°' : ''}</text>
      </svg>
      <div class="hv-dial__center">
        {widget.caption && <span class="hv-dial__caption">{widget.caption}</span>}
        <span class="hv-dial__value">
          {value.num === undefined ? '–' : fmtNum(value.num, decimals)}
          {unit && <span class={degree ? 'hv-dial__deg' : 'hv-dial__unit'}>{degree ? '°' : unit}</span>}
        </span>
        {widget.markerPoint && (
          <span class="hv-dial__sub"><i />{widget.markerLabel ?? 'Ist'} {fmtNum(marker.num, decimals)}{degree ? '°' : ''}</span>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------ day schedule

/** "06:00-22:00, --, --" (BSB) oder "06:00 (Ein); 22:00 (Aus)" (Synco) in
 *  Minutenbereiche [von, bis). Null, wenn der Text nichts hergibt. */
export function parseDaySchedule(text: unknown): [number, number][] | null {
  if (typeof text !== 'string') return null;
  const toMin = (s: string) => { const [h, m] = s.split(':').map(Number); return h! * 60 + m!; };
  const ranges = [...text.matchAll(/(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})/g)];
  if (ranges.length > 0) {
    return ranges.map((m) => {
      const a = toMin(m[1]!), b = toMin(m[2]!);
      return [a, b === 0 ? 1440 : b] as [number, number];
    }).filter(([a, b]) => b > a);
  }
  const ev = [...text.matchAll(/(\d{1,2}:\d{2})\s*\((Ein|Aus)\)/g)].map((m) => ({ t: toMin(m[1]!), on: m[2] === 'Ein' }));
  if (ev.length === 0) return text.includes('--') ? [] : null;
  const out: [number, number][] = [];
  let start: number | null = null;
  for (const e of ev) {
    if (e.on && start === null) start = e.t;
    else if (!e.on && start !== null) { out.push([start, e.t]); start = null; }
  }
  if (start !== null) out.push([start, 1440]);
  return out;
}

const hhmm = (min: number) => `${String(Math.floor(min / 60) % 24).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
const DAYS = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

export function DayScheduleRenderer({ widget, source }: P<DayScheduleWidget>) {
  const now = useMinuteNow();
  const day = now.getDay();
  const keys = [widget.sundayPoint, widget.mondayPoint, widget.tuesdayPoint, widget.wednesdayPoint,
    widget.thursdayPoint, widget.fridayPoint, widget.saturdayPoint];
  const today = usePoint(source, keys[day]);
  const mode = usePoint(source, widget.modePoint);
  const modeMeta = useLeafMeta(widget.modePoint);
  const periods = parseDaySchedule(today.raw);
  const min = now.getHours() * 60 + now.getMinutes();
  const auto = widget.modePoint === undefined || widget.modePoint === '' || mode.num === (widget.autoValue ?? 1);
  const next = periods
    ? periods.flatMap(([a, b]) => [{ t: a, to: 'Komfort' }, { t: b, to: 'Reduziert' }])
      .filter((e) => e.t > min && e.t < 1440).sort((x, y) => x.t - y.t)[0]
    : undefined;

  let foot: preact.ComponentChildren;
  if (!periods) foot = 'Zeitprogramm wird geladen';
  else if (!auto) foot = `Zeitprogramm ruht, Betriebsart ${formatLeafValue(mode.raw, modeMeta, 0)}`;
  else if (next) foot = <>Nächster Wechsel <b>{hhmm(next.t)}</b> auf {next.to}</>;
  else foot = 'Heute kein weiterer Wechsel';

  return (
    <div class="hv-dsched">
      <div class="hv-dsched__top">
        <span class="hv-dsched__title">{widget.title ?? 'Zeitprogramm heute'}</span>
        <span class="hv-dsched__day">{DAYS[day]}</span>
      </div>
      <div class={`hv-dsched__bar${auto ? '' : ' hv-dsched__bar--idle'}`}>
        {(periods ?? []).map(([a, b]) => (
          <div key={a} class="hv-dsched__on" style={{ left: `${(a / 1440) * 100}%`, width: `${((b - a) / 1440) * 100}%` }}>
            <span>{hhmm(a)}</span>
          </div>
        ))}
        <div class="hv-dsched__now" style={{ left: `${(min / 1440) * 100}%` }} />
      </div>
      <div class="hv-dsched__axis"><span>0</span><span>6</span><span>12</span><span>18</span><span>24 h</span></div>
      <div class="hv-dsched__foot">{foot}</div>
    </div>
  );
}

// ------------------------------------------------------------ heating curve

/** Vereinfachte Kennlinie wie in der TP8-Demo: eine Gerade durch den
 *  Raumsollwert. Der RVS rechnet leicht gekruemmt; fuer die Anzeige genuegt das. */
export function flowSetpoint(slope: number, shift: number, room: number, outside: number): number {
  return room + shift + slope * (room - outside);
}

export function HeatingCurveRenderer({ widget, source }: P<HeatingCurveWidget>) {
  const slope = usePoint(source, widget.slopePoint).num;
  const shift = usePoint(source, widget.shiftPoint).num ?? 0;
  const room = usePoint(source, widget.roomSetpointPoint).num ?? 21;
  const outside = usePoint(source, widget.outsidePoint).num;
  const summer = usePoint(source, widget.summerPoint).num;
  const flowSet = usePoint(source, widget.flowSetpointPoint).num;
  const flowIs = usePoint(source, widget.flowPoint).num;

  // Flach wie die Karte, sonst bleibt die Grafik zwischen breiten Raendern klein.
  const W = 700, H = 130, PADL = 44, PADB = 26, PADT = 8, PADR = 12;
  const X0 = 20, X1 = -15, Y0 = 20, Y1 = 60;
  const x = (t: number) => PADL + ((X0 - t) / (X0 - X1)) * (W - PADL - PADR);
  const y = (v: number) => H - PADB - ((v - Y0) / (Y1 - Y0)) * (H - PADB - PADT);
  const clampY = (v: number) => Math.min(Y1, Math.max(Y0, v));
  let line = '';
  if (slope !== undefined) {
    for (let t = X0; t >= X1; t -= 1) line += `${t === X0 ? 'M' : 'L'} ${x(t)} ${y(clampY(flowSetpoint(slope, shift, room, t)))} `;
  }
  const now = slope !== undefined && outside !== undefined ? clampY(flowSetpoint(slope, shift, room, outside)) : null;

  return (
    <div class="hv-curve">
      <div class="hv-curve__top">
        <span class="hv-curve__title">{widget.title ?? 'Heizkennlinie'}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} class="hv-curve__svg">
        {[20, 40, 60].map((v) => (
          <g key={v}>
            <line x1={PADL} x2={W - PADR} y1={y(v)} y2={y(v)} class="hv-curve__grid" />
            <text x={PADL - 10} y={y(v) + 6} text-anchor="end" class="hv-curve__ax">{v}°</text>
          </g>
        ))}
        {[20, 10, 0, -10].map((t) => <text key={t} x={x(t)} y={H - 6} text-anchor="middle" class="hv-curve__ax">{t}°</text>)}
        {summer !== undefined && summer <= X0 && summer >= X1 && (
          <g>
            <rect x={x(X0)} y={PADT} width={x(summer) - x(X0)} height={H - PADB - PADT} class="hv-curve__summer" />
            <line x1={x(summer)} x2={x(summer)} y1={PADT} y2={H - PADB} class="hv-curve__summer-line" />
          </g>
        )}
        <path d={line} fill="none" stroke="var(--accent)" stroke-width="4" stroke-linejoin="round" />
        {now !== null && outside !== undefined && (
          <g>
            <line x1={x(outside)} x2={x(outside)} y1={y(now)} y2={H - PADB} class="hv-curve__now-line" />
            <circle cx={x(outside)} cy={y(now)} r="9" class="hv-curve__now" />
          </g>
        )}
      </svg>
      <div class="hv-curve__facts">
        <span>Steilheit <b>{fmtNum(slope, 2)}</b></span>
        {widget.summerPoint && <span>Sommer ab <b>{fmtNum(summer, 0)}°</b></span>}
        {widget.flowSetpointPoint && <span>Vorlauf soll <b>{fmtNum(flowSet, 1)}°</b></span>}
        {widget.flowPoint && <span>ist <b>{fmtNum(flowIs, 1)}°</b></span>}
      </div>
    </div>
  );
}

// ------------------------------------------------------------ tank

/** Farbe einer Wassertemperatur: kalt blau, lau gelb, heiss rot. */
function tempColour(t: number): string {
  const stops: [number, [number, number, number]][] = [
    [15, [74, 168, 255]], [35, [227, 163, 59]], [55, [224, 57, 63]], [70, [176, 24, 32]],
  ];
  if (t <= stops[0]![0]) return `rgb(${stops[0]![1].join(',')})`;
  for (let i = 1; i < stops.length; i++) {
    const [t1, c1] = stops[i]!;
    const [t0, c0] = stops[i - 1]!;
    if (t <= t1) {
      const k = (t - t0) / (t1 - t0);
      return `rgb(${c0.map((c, j) => Math.round(c + (c1[j]! - c) * k)).join(',')})`;
    }
  }
  return `rgb(${stops[stops.length - 1]![1].join(',')})`;
}

export function TankRenderer({ widget, source }: P<TankWidget>) {
  const top = usePoint(source, widget.topPoint);
  const bottom = usePoint(source, widget.bottomPoint);
  const set = usePoint(source, widget.setpointPoint);
  const named = useNamedPoints(widget.points, source);
  const charging = evaluateCondition(widget.chargingWhen, named.ctx);
  const t = top.num;
  const b = bottom.num ?? (t !== undefined ? t - 8 : undefined);
  const gid = `hv-tank-g-${widget.id}`;
  return (
    <div class={`hv-tank${charging ? ' hv-tank--charging' : ''}`}>
      <svg viewBox="0 0 120 220" class="hv-tank__svg" aria-hidden="true">
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" style={{ stopColor: t !== undefined ? tempColour(t) : 'var(--surface-elevated)' }} />
            <stop offset="0.55" style={{ stopColor: t !== undefined && b !== undefined ? tempColour((t + b) / 2) : 'var(--surface-elevated)' }} />
            <stop offset="1" style={{ stopColor: b !== undefined ? tempColour(b) : 'var(--surface-elevated)' }} />
          </linearGradient>
        </defs>
        <rect x="14" y="8" width="92" height="204" rx="40" class="hv-tank__shell" />
        <rect x="20" y="14" width="80" height="192" rx="34" fill={`url(#${gid})`} class="hv-tank__water" />
        {charging && <path d="M20 150 q20 -10 40 0 t40 0" class="hv-tank__wave" />}
      </svg>
      <div class="hv-tank__labels">
        {widget.caption && <span class="hv-tank__caption">{widget.caption}</span>}
        <span class="hv-tank__t">oben <b>{fmtNum(t, 0)}°</b></span>
        {widget.bottomPoint && <span class="hv-tank__t">unten <b>{fmtNum(bottom.num, 0)}°</b></span>}
        {widget.setpointPoint && <span class="hv-tank__t">Soll <b>{fmtNum(set.num, 0)}°</b></span>}
        {charging && <span class="hv-tank__state">lädt</span>}
      </div>
    </div>
  );
}
